import type { ReleaseStatusData } from './types';

import { useQuery } from '@tanstack/react-query';
import ms from 'ms';

import { unique } from '@vestigas/utils';

import fallbackJson from './release-status.fallback.json';

const fallbackData = fallbackJson as ReleaseStatusData;

/**
 * Try to load the generated release-status.json (gitignored, created at build
 * time). Falls back to the committed empty-state fallback when the file has not
 * been generated (e.g. local dev without a prior build step).
 */
async function loadReleaseStatusData(): Promise<ReleaseStatusData> {
  try {
    const files = import.meta.glob<{ default: ReleaseStatusData }>(
      './release-status.json',
    );
    const loader = files['./release-status.json'];

    if (!loader) {
      return fallbackData;
    }

    const mod = await loader();

    return mod.default;
  } catch {
    return fallbackData;
  }
}

/** Collect all unique Linear issue IDs from the static build-time data. */
function collectIssueIds(data: ReleaseStatusData): string[] {
  const ids = new Set<string>();

  for (const release of data.releases) {
    for (const issue of release.issues) {
      ids.add(issue.id);
    }
  }

  return [...ids];
}

type LinearIssueData = {
  description: string | null;
  labels: string[];
  missingTestInstructions: boolean;
  project: string | null;
  status: string;
  statusColor: string | null;
  statusType: string;
  title: string;
};

// ---------------------------------------------------------------------------
// Linear GraphQL fetch
// ---------------------------------------------------------------------------

/**
 * Fetch fresh issue data from the Linear GraphQL API.
 * Mirrors the query used in generate-release-status.mjs.
 *
 * Currently disabled; will be proxied through the backend.
 */
async function fetchLinearIssues(
  _issueIds: string[],
): Promise<Map<string, LinearIssueData>> {
  // const result = new Map<string, LinearIssueData>();
  // for (const issue of json.data?.issues?.nodes ?? []) {
  //   const rawDesc = issue.description ?? '';
  //   const descriptionSummary =
  //     rawDesc
  //       .split(/\r?\n/)
  //       .slice(0, 2)
  //       .map((s) => s.trim())
  //       .filter(Boolean)
  //       .join(' ') || null;

  //   const hasTestInstructions = /how to test|wie testen/i.test(rawDesc);

  //   result.set(issue.identifier, {
  //     title: issue.title,
  //     description: descriptionSummary,
  //     status: issue.state?.name ?? 'Unknown',
  //     statusType: issue.state?.type ?? 'unstarted',
  //     statusColor: issue.state?.color ?? null,
  //     labels: (issue.labels?.nodes ?? []).map((l) => l.name),
  //     project: issue.project?.name ?? null,
  //     missingTestInstructions: !hasTestInstructions,
  //   });
  // }

  return new Map();
}

// ---------------------------------------------------------------------------
// Merge
// ---------------------------------------------------------------------------

/** Overlay fresh Linear issue data onto the static build-time release data. */
function mergeLinearData(
  baseData: ReleaseStatusData,
  linearIssues: Map<string, LinearIssueData>,
): ReleaseStatusData {
  return {
    ...baseData,
    linearEnriched: linearIssues.size > 0,
    linearFetchedAt: new Date().toISOString(),
    releases: baseData.releases.map((release) => ({
      ...release,
      issues: release.issues.map((issue) => {
        const fresh = linearIssues.get(issue.id);
        if (!fresh) {
          return issue;
        }

        return {
          ...issue,
          ...fresh,
          mrNumber: issue.mrNumber, // Preserve mrNumber — it comes from git merge commits, not Linear
        };
      }),
    })),
  };
}

/**
 * Provides release status data, optionally enriched with live Linear updates.
 *
 * Loads the generated build-time JSON asynchronously (falling back to the
 * committed empty-state JSON when the file hasn't been generated). Live Linear
 * updates are disabled until a backend proxy is in place.
 */
export function useReleaseStatusData() {
  const { data: staticData = fallbackData } = useQuery({
    queryKey: ['releaseStatus', 'staticData'],
    queryFn: loadReleaseStatusData,
    staleTime: Infinity,
  });

  const issueIds = collectIssueIds(staticData);

  const {
    data: linearIssues,
    dataUpdatedAt,
    isFetching,
  } = useQuery({
    enabled: false, // TODO: update this when the Linear backend proxy is in place
    queryFn: () => fetchLinearIssues(issueIds),
    queryKey: [
      'releaseStatus',
      'linearIssues',
      {
        issueIds: unique([...issueIds].toSorted()),
      },
    ],
    refetchInterval: ms('5m'),
  });

  const data =
    linearIssues && linearIssues.size > 0
      ? mergeLinearData(staticData, linearIssues)
      : staticData;

  return {
    data,
    isLiveRefreshEnabled: false,
    isRefreshing: isFetching,
    lastRefreshedAt: dataUpdatedAt > 0 ? new Date(dataUpdatedAt) : null,
  };
}
